/**
 * The report engine's, the close's and the event log's payload shapes
 * (spec §8, §9, §4.4).
 *
 * Same convention as `types-accounting.ts`: every field optional with an index
 * signature, because these are ProductData's shapes and a renamed column should
 * blank one cell rather than break a screen. Every `*Minor` is an INTEGER of
 * minor units, and every report figure is already PRESENTED — nothing here may
 * flip a sign.
 *
 * Its own module rather than more of `types-accounting.ts`: that file is on the
 * 500-line lint ratchet and is being extended by plans 14 and 15 at the same
 * time. It re-exports this one, so `@/types-accounting` stays the one import.
 */

/** Spec §8.1's `ReportName`. The path segment and the UI key are the same word. */
export type ReportKey =
  | "profit_and_loss"
  | "balance_sheet"
  | "trial_balance"
  | "general_ledger"
  | "journal"
  | "cash_summary"
  | "account_transactions";

export interface ReportColumnDef {
  key?: string;
  label?: string;
  kind?: "text" | "money" | "date" | "link";
  [key: string]: unknown;
}

export interface ReportRowDef {
  cells?: Record<string, string | number | null>;
  /** Present on a row that IS an account — what makes the amount a drill-down. */
  accountId?: string;
  entryId?: string;
  depth?: number;
  isTotal?: boolean;
  isContra?: boolean;
  [key: string]: unknown;
}

export interface ReportResultPayload {
  report?: string;
  title?: string;
  columns?: ReportColumnDef[];
  rows?: ReportRowDef[];
  totals?: Record<string, number>;
  range?: { from?: string; to?: string; asAt?: string };
  /** Periods close at year end, so anything short of that is a snapshot. */
  provisional?: boolean;
  disclaimer?: string;
  /** Only the cash basis carries these, and the UI must show them. */
  caveats?: string[];
  nextCursor?: string | null;
  [key: string]: unknown;
}

/** Spec §9.4 — the four gross figures plus the 6900 lines. */
export interface Form5472SupportResponse {
  /** 3100 and 3200 for the year, NEVER netted against each other. */
  contributionsGrossMinor?: number;
  distributionsGrossMinor?: number;
  /** 1500 and 2600 at year end. */
  dueFromMemberMinor?: number;
  dueToMemberMinor?: number;
  ownerPersonalExpenses?: Array<{
    entryId?: string;
    date?: string;
    description?: string;
    amountMinor?: number;
  }>;
  relatedPartyLines?: Array<{
    contactId?: string;
    displayName?: string;
    accountCode?: string;
    amountMinor?: number;
    f5472Category?: string | null;
  }>;
  provisional?: boolean;
  disclaimer?: string;
  [key: string]: unknown;
}

export type VendorFormHint =
  | "1099-NEC review"
  | "1042-S review"
  | "processor-reported (1099-K)"
  | "no form expected"
  | "unknown — ask";

export interface VendorFormRow {
  contactId?: string;
  displayName?: string;
  usPerson?: boolean | null;
  taxForm?: string;
  servicesInUs?: boolean | null;
  totalMinor?: number;
  byRail?: Record<string, number>;
  /** A rule surfaced, never a conclusion. The preparer decides. */
  formHint?: VendorFormHint | string;
  [key: string]: unknown;
}

export interface VendorFormsResponse {
  rows?: VendorFormRow[];
  /** Rendered, never hardcoded: the NEC threshold moved for CY2026. */
  thresholds?: { necMinor?: number };
  disclaimer?: string;
  [key: string]: unknown;
}

export interface YearCloseRow {
  id?: string;
  fiscalYear?: number;
  status?: "started" | "swept" | "posted" | "packaged" | "closed" | "reopened";
  closingEntryId?: string | null;
  packageDocumentId?: string | null;
  preparerNotes?: string | null;
  startedBy?: string;
  startedAt?: string;
  closedAt?: string | null;
  /**
   * `GET …/close` maps this column, and no live path can ever set it: a closed
   * financial year cannot be reopened. Declared so an existing row renders
   * rather than disappearing; `reopen_reason` and `reopened_by` are NOT, because
   * that route answers 403 and the read does not map them either.
   */
  reopenedAt?: string | null;
  [key: string]: unknown;
}

export interface YearClosesResponse {
  closes?: YearCloseRow[];
  [key: string]: unknown;
}

/**
 * `GET …/books/:bookId/locks` — the book's two lock dates, and their own read.
 *
 * NOT part of `YearClosesResponse`: the close list answers `{ closes }` alone,
 * so a form reading the dates off it shows "—" for both, never fires the
 * forward-only guard, and turns Save into "clear both locks".
 */
export interface LockDatesResponse {
  softLockBefore?: string | null;
  hardLockBefore?: string | null;
  [key: string]: unknown;
}

/** One bank, card or processor account's standing on 31 December. */
export interface CloseReadinessAccount {
  code: string;
  name: string;
  reconciledAtYearEnd: boolean;
  /** Currency of `differenceMinor`; absent on old responses (USD). */
  ccy?: string;
  /** Statement minus books; null when the bank never gave a balance. */
  differenceMinor?: number | null;
}

/** Spec §9.2's `CloseReadiness`. */
export interface CloseReadinessResponse {
  fiscalYear?: number;
  unreconciledLines?: number;
  openQuestions?: number;
  proposedTransfers?: number;
  quarantined?: number;
  integrityFindings?: number;
  softLockBefore?: string | null;
  ready?: boolean;
  /** Round 3 (R7): the checklist's own numbers, top-level beside the old object. */
  unreviewedCount?: number;
  openQuestionCount?: number;
  accounts?: CloseReadinessAccount[];
  /** Plain sentences. Rendered verbatim; the console invents no blocker. */
  blockers?: string[];
  [key: string]: unknown;
}

/*
 * `BookkeepingEventRow`, `BookkeepingNoteRow`, `EventsResponse` and
 * `SubjectHistoryResponse` are NOT declared here. Plans 14 and 15 already put
 * them in `types-accounting.ts`, and a second declaration of the same row would
 * be two names for one payload — the failure this module's own header warns
 * about. Import them from `@/types-accounting` like everything else.
 */

/**
 * The event kinds the History tab offers as a filter, grouped as it shows them.
 *
 * The VALUE is a prefix and the URL keeps it that way, because spec §4.4 lists
 * forty-odd kinds and a dropdown of forty is not a filter. `listEvents` matches
 * `kind IN (…)` exactly and has no prefix clause, so `kinds` holds the kinds
 * each prefix stands for and `expandEventKinds` is what the request carries —
 * sending the prefix itself asks for a kind no row has ever had, which answers
 * an empty feed and looks like a book with no history.
 */
export const EVENT_KIND_FILTERS: ReadonlyArray<{
  value: string;
  label: string;
  kinds: readonly string[];
}> = [
  {
    value: "line.",
    label: "Transactions",
    kinds: [
      "line.auto_posted",
      "line.confirmed",
      "line.asked_customer",
      "line.answered",
      "line.excluded",
      "line.included",
      "line.recoded",
    ],
  },
  {
    value: "transfer.",
    label: "Transfers",
    kinds: [
      "transfer.proposed",
      "transfer.confirmed",
      "transfer.rejected",
      "transfer.released",
    ],
  },
  {
    value: "rule.",
    label: "Rules",
    kinds: [
      "rule.created",
      "rule.updated",
      "rule.enabled",
      "rule.disabled",
      "rule.reordered",
      "rule.applied",
    ],
  },
  {
    value: "contact.",
    label: "Contacts",
    kinds: ["contact.created", "contact.updated", "contact.merged"],
  },
  {
    value: "account.",
    label: "Chart of accounts",
    // `chart.topped_up` is the same subject under a different prefix: a book
    // that already existed being given the accounts added after it was seeded.
    kinds: [
      "account.created",
      "account.updated",
      "account.archived",
      "chart.topped_up",
    ],
  },
  {
    value: "journal.",
    label: "Journals",
    kinds: ["journal.posted", "journal.reversed", "journal.attachment_added"],
  },
  { value: "note.", label: "Notes", kinds: ["note.added"] },
  {
    value: "lock.",
    label: "Lock dates",
    kinds: ["lock.soft_set", "lock.hard_set", "lock.soft_overridden"],
  },
  {
    value: "period.",
    label: "Periods",
    kinds: ["period.closed", "period.reopened"],
  },
  {
    value: "year.",
    label: "Year end",
    kinds: ["year.closed", "year.package_generated"],
  },
  { value: "trueup.", label: "True-ups", kinds: ["trueup.posted"] },
  {
    value: "connection.",
    label: "Connections",
    kinds: [
      "connection.linked",
      "connection.relinked",
      "connection.revoked",
      "connection.removed",
      "connection.new_accounts",
    ],
  },
  {
    value: "import.",
    label: "Imports",
    kinds: [
      "import.uploaded",
      "import.mapped",
      "import.committed",
      "import.discarded",
    ],
  },
  {
    value: "stripe.",
    label: "Stripe",
    kinds: ["stripe.connected", "stripe.synced"],
  },
];

/**
 * A `kinds` filter as the API wants it: exact kinds, comma-separated.
 *
 * Anything that is not one of the prefixes above passes through untouched, so a
 * link carrying a single exact kind still works.
 */
export function expandEventKinds(value: string): string {
  const group = EVENT_KIND_FILTERS.find((filter) => filter.value === value);
  return group ? group.kinds.join(",") : value;
}

/** Round 4 (M1): `GET …/reports/periodic`. Every field optional at runtime. */
export type PeriodicReportKey =
  "profit_and_loss" | "balance_sheet" | "cash_flow";
export type PeriodicGrain = "month" | "quarter" | "year";
export type PeriodicBasis = "accrual" | "cash";

export interface PeriodicColumn {
  /** '2026-01' for a month; the API names quarter and year keys. */
  key: string;
  label: string;
  from?: string;
  to?: string;
  /** Month to date: labelled "Oct MTD" on screen. */
  partial?: boolean;
}

export interface PeriodicRow {
  id: string;
  kind: "section" | "group" | "account" | "total";
  depth: number;
  label: string;
  accountId?: string;
  code?: string;
  values?: Record<string, number | null>;
  total?: number | null;
  collapsible?: boolean;
  parentId?: string | null;
}

/** Arrays aligned to `columns`; which ones are set depends on the report. */
export interface PeriodicChart {
  kind?: PeriodicReportKey;
  revenue?: number[];
  expenses?: number[];
  netProfit?: number[];
  workingCapital?: number[];
  currentAssets?: number[];
  longTermAssets?: number[];
  liabilities?: number[];
  equity?: number[];
  endingBalance?: number[];
  movement?: number[];
}

/** Settled bank lines in the report's window that have not reached the ledger (NT-67). */
export interface PeriodicUnposted {
  feedAccountId: string;
  accountId: string;
  code: string;
  name: string;
  count: number;
}

export interface PeriodicReportResponse {
  report?: PeriodicReportKey;
  grain?: PeriodicGrain;
  /** null for the balance sheet and cash flow. */
  basis?: PeriodicBasis | null;
  year?: number;
  currency?: string;
  columns?: PeriodicColumn[];
  totalColumn?: { key: string; label: string } | null;
  rows?: PeriodicRow[];
  chart?: PeriodicChart;
  provisional?: boolean;
  disclaimer?: string;
  caveats?: string[];
  /** Empty or absent when every settled line in the window is posted. */
  unposted?: PeriodicUnposted[];
}

export type GeneralLedgerPeriod =
  "this_month" | "last_month" | "this_year" | "custom";
